import { useEffect, useMemo, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { invokeProvidedBrandSpecificCode, MAKE } from 'next-common';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import {
  selectConfiguration,
  selectCurrentStep,
  selectDealerIds,
  selectIsVehicleFiltersCountLoading,
  selectOrder,
  selectSelectableEquipments,
  selectSelectedVehicleFilters,
  selectVehicleFilterCounts,
  selectVehicleList,
} from '@store/selectors';
import {
  resetVehicleFiltersWithCounts,
  setExactMatchCount,
  setIsFiltersCountLoading,
  setNonExactMatchCount,
  setVehicleCountBySeller,
  setVehicleFiltersWithCounts,
} from '@store/slices/salesWizardSlice';
import { useClientLazyQuery, FILTER_FETCH_STEP, IVehicleCountBySeller } from '@common/index';
import { NEXT_ENTITY } from '@common/interfaces';
import { PageSteps } from '@components/sales/sales-common/NewHeader';
import { buildExactMatchCodeBase, createKiaStrategy, createNonKiaStrategy, mapFilterCountsData } from '@utils/stockCar';
import { calculateExactAndNonExactMatchCounts, getStructuredVehicleData, getVehicleCountBySeller } from '@utils/index';
import { importerLocationIds } from '@utils/sales/vehicleStatus';
import { selectModelSeriesMapping } from '@store/selectors/modelSeriesSelector';
import { isEmpty, isEqual } from 'lodash';

export const useVehicleFilterCounts = () => {
  const dispatch = useAppDispatch();
  const pathname = usePathname();

  const currentStep = useAppSelector(selectCurrentStep);
  const dealerIds = useAppSelector(selectDealerIds);
  const vehicleList = useAppSelector(selectVehicleList);
  const selectedVehicleFilters = useAppSelector(selectSelectedVehicleFilters);
  const vehicleFiltersWithCounts = useAppSelector(selectVehicleFilterCounts);
  const isFiltersCountLoading = useAppSelector(selectIsVehicleFiltersCountLoading);
  const selectableEquipments = useAppSelector(selectSelectableEquipments);
  const modelSeriesMappingData = useAppSelector(selectModelSeriesMapping);
  const order = useAppSelector(selectOrder);
  const configuration = useAppSelector(selectConfiguration);
  const applicationCodes = configuration?.features?.vehicleTypeFilter ?? [];

  const inFlightKeyRef = useRef<string | null>(null);
  const prevFiltersRef = useRef<any>(null);
  const hasModelVariantRef = useRef(false);
  const hasAllFiltersRef = useRef(false);
  const exactMatchCodeRef = useRef<string[]>([]);
  const [exactMatchCodeStr, setExactMatchCodeStr] = useState<string[]>([]);
  const prevCartRef = useRef<{ exact: number; nonExact: number; seller: IVehicleCountBySeller } | null>(null);

  const isOwnLocation = (locationId: string) => dealerIds?.includes(locationId?.toString());
  const isImporterLocation = (locationId: string) => importerLocationIds?.includes(locationId?.toString());

  const structuredVehicle = useMemo(() => getStructuredVehicleData(vehicleList)?.[0], [vehicleList]);

  const cartCounts = useMemo(() => {
    const hasCountsSource = !!vehicleFiltersWithCounts?.exactMatchCode || !!vehicleFiltersWithCounts?.locationId;
    if (!hasCountsSource) {
      return {
        exactMatchCount: 0,
        nonExactMatchCount: 0,
        sellerCount: {} as IVehicleCountBySeller,
        ready: false,
      };
    }

    // Calculate and update Exact & Non-Exact Match counts
    const { exactMatchCount, nonExactMatchCount } = calculateExactAndNonExactMatchCounts(
      exactMatchCodeStr,
      vehicleFiltersWithCounts.exactMatchCode
    );

    // Calculate and update vehicle counts by seller type
    const sellerCount = getVehicleCountBySeller(vehicleFiltersWithCounts.locationId, {
      isImporterLocation,
      isOwnLocation,
    });

    return {
      exactMatchCount,
      nonExactMatchCount,
      sellerCount,
      ready: true,
    };
  }, [
    exactMatchCodeStr,
    vehicleFiltersWithCounts?.exactMatchCode,
    vehicleFiltersWithCounts?.locationId,
    isImporterLocation,
    isOwnLocation,
  ]);

  // Fetch vehicle filter counts based on selected filters
  const [getVehicleFiltersWithCount] = useClientLazyQuery(NEXT_ENTITY.VEHICLE_STOCK_FILTERS_COUNT, {
    onCompleted: (data, options) => {
      const responseKey = options?.context?.requestKey;
      const step = options?.context?.step;

      // Ignore stale responses
      if (responseKey !== inFlightKeyRef.current) return;

      if (!data?.filterResults?.length) {
        inFlightKeyRef.current = null;
        dispatch(setIsFiltersCountLoading(false));
        return;
      }

      const filtersWithCounts = mapFilterCountsData(data.filterResults);
      dispatch(setVehicleFiltersWithCounts(filtersWithCounts));

      // let cart counts effect do clear loading after updating counts
      inFlightKeyRef.current = null;

      if (step === FILTER_FETCH_STEP.MODEL_VARIANT) {
        hasModelVariantRef.current = true;
      }

      if (step === FILTER_FETCH_STEP.ALL_FILTERS) {
        hasAllFiltersRef.current = true;
      }
    },
    onError: (error) => {
      console.error('An error occured while fetching vehicle filter counts:', error);
      inFlightKeyRef.current = null;
      dispatch(setIsFiltersCountLoading(false));
    },
  });

  // Fetch filter counts
  useEffect(() => {
    const stopLoadingIfIdle = () => {
      if (isFiltersCountLoading && !inFlightKeyRef.current) {
        dispatch(setIsFiltersCountLoading(false));
      }
    };

    const factoryDetails = order?.vehicles?.[0]?.orderFromFactoryDetails;
    const hasFactoryDetails = !!factoryDetails && Object.keys(factoryDetails)?.length > 0;
    const isBilPage = pathname?.includes('/bil');

    if ((hasFactoryDetails && !isBilPage) || !isEmpty(order?.vehicles?.[0]?.serialNo)) {
      stopLoadingIfIdle();
      return;
    }

    if (!order || (!selectedVehicleFilters?.brand && !selectedVehicleFilters?.modelSeries)) {
      stopLoadingIfIdle();
      return;
    }

    // Skip if the selectedVehicleFilters values are deeply equal
    if (prevFiltersRef.current && isEqual(prevFiltersRef.current, selectedVehicleFilters)) {
      stopLoadingIfIdle();
      return;
    }
    prevFiltersRef.current = selectedVehicleFilters;

    const isKia = invokeProvidedBrandSpecificCode(MAKE.Kia, { order });
    const params = {
      order,
      selectVehicleFilters: selectedVehicleFilters,
      hasFetchedModelVariantCounts: hasModelVariantRef.current,
      applicationCodes,
      modelSeriesMappingData,
      dataFromFactoryEquipLevels: selectedVehicleFilters?.dataFromFactoryEquipLevels,
    };

    const strategy = isKia ? createKiaStrategy(params) : createNonKiaStrategy(params);

    const decision = strategy.shouldFetch();
    if (!decision) {
      if (isKia) {
        dispatch(resetVehicleFiltersWithCounts());
        setExactMatchCodeStr([]);
      }
      stopLoadingIfIdle();
      return;
    }

    const payload = strategy.buildPayload(decision.step);
    const payloadKey = JSON.stringify({ step: decision.step, filters: payload.filters });

    // Skip if not START tab and ALL_FILTERS already fetched
    if (currentStep !== PageSteps.START && hasAllFiltersRef.current) {
      stopLoadingIfIdle();
      return;
    }

    // Prevent duplicate requests
    if (payloadKey === inFlightKeyRef.current) return;
    inFlightKeyRef.current = payloadKey;

    const overrides = strategy.getExactMatchOverrides?.();
    exactMatchCodeRef.current =
      buildExactMatchCodeBase({
        selectVehicleFilters: selectedVehicleFilters,
        modelIdOverride: overrides?.modelId,
        extraSkus: overrides?.skus,
        structuredVehicle,
      }) ?? [];

    setExactMatchCodeStr(exactMatchCodeRef.current);
    dispatch(setIsFiltersCountLoading(true));

    console.log('Fetching vehicle filter counts...');

    getVehicleFiltersWithCount({
      variables: payload,
      context: { requestKey: payloadKey, step: decision.step },
    });
  }, [
    selectedVehicleFilters,
    order,
    currentStep,
    applicationCodes,
    modelSeriesMappingData,
    structuredVehicle,
    selectableEquipments,
    vehicleList,
    pathname,
  ]);

  // Update cart counts when filter counts and model code are ready
  useEffect(() => {
    if (!cartCounts.ready) return;

    const current = {
      exact: cartCounts.exactMatchCount,
      nonExact: cartCounts.nonExactMatchCount,
      seller: cartCounts.sellerCount,
    };

    // Only dispatch when values actually change to avoid repeated renders/logs
    if (prevCartRef.current && isEqual(prevCartRef.current, current)) {
      if (!inFlightKeyRef.current) {
        dispatch(setIsFiltersCountLoading(false));
      }
      return;
    }

    prevCartRef.current = current;

    dispatch(setExactMatchCount(cartCounts.exactMatchCount));
    dispatch(setNonExactMatchCount(cartCounts.nonExactMatchCount));
    dispatch(setVehicleCountBySeller(cartCounts.sellerCount));

    // Set loading to false when cart counts are updated
    if (!inFlightKeyRef.current) {
      dispatch(setIsFiltersCountLoading(false));
    }
  }, [cartCounts]);
};
